import { useQueries } from '@tanstack/react-query';
import { GetItemPreviewQueryKey, sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { SeeqNames } from '@seeqdev/constants';

/**
 * Resolves display names for workbook ids, skipping any whose name the caller already knows.
 *
 * Content in the report store carries no workbook name — `formatContentFromApiOutput` never sets one, and the name
 * the bulk-edit modal shows comes from the much heavier `getContentsWithAllMetadata`. Item previews are small and
 * cached per id by react-query, so looking up only the stragglers costs far less than that endpoint.
 */
export const useWorkbookNames = (
  workbookIds: string[],
  knownNamesById: Record<string, string> = {},
): Record<string, string> => {
  const missingIds = [...new Set(workbookIds)].filter((id) => !knownNamesById[id]).sort();

  return useQueries({
    queries: missingIds.map((id) => ({
      queryKey: GetItemPreviewQueryKey({ id, includeProperties: [SeeqNames.Properties.Name] }),
      queryFn: () =>
        sqItemsApi
          .getItemPreview({ id, includeProperties: [SeeqNames.Properties.Name] })
          .then((response) => response.data),
      staleTime: Infinity,
      retry: false,
    })),
    combine: (results) => {
      const resolved: Record<string, string> = { ...knownNamesById };
      missingIds.forEach((id, index) => {
        const name = results[index]?.data?.name;
        if (name) {
          resolved[id] = name;
        }
      });
      return resolved;
    },
  });
};
